'use client';

import React, { useState, useEffect } from 'react';
import ContributionCard from './ContributionCard';
import ProgressTracker from './ProgressTracker';
import CulturalContext from './CulturalContext';
import PaymentModal from './PaymentModal';
import SuccessMessage from './SuccessMessage';

interface ContributionCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  suggestedAmounts: number[];
  current: number;
  goal: number;
  contributors: number;
}

const SupportInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [categories, setCategories] = useState<ContributionCategory[]>([
    {
      id: 'dowry',
      title: 'Traditional Dowry',
      description: 'Contribute to the bride price, a sacred covenant between families that symbolizes respect, commitment, and community blessing.',
      icon: 'HeartIcon',
      suggestedAmounts: [100, 250, 500, 1000],
      current: 12750,
      goal: 25000,
      contributors: 47,
    },
    {
      id: 'ceremony',
      title: 'Wedding Ceremony',
      description: 'Support the sacred celebration including venue, decorations, catering, and all elements that create a beautiful worship experience for our union.',
      icon: 'SparklesIcon',
      suggestedAmounts: [50, 100, 250, 500],
      current: 8420,
      goal: 15000,
      contributors: 63,
    },
    {
      id: 'household',
      title: 'Future Household',
      description: 'Help establish our new home with essentials, furniture, and items that will create a Godly foundation for our family life together.',
      icon: 'HomeIcon',
      suggestedAmounts: [25, 50, 100, 250],
      current: 3890,
      goal: 10000,
      contributors: 52,
    },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState<{
    category: string;
    amount: number;
  } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleContribute = (categoryId: string, amount: number) => {
    const category = categories.find((c) => c.id === categoryId);
    if (category) {
      setSelectedContribution({ category: category.title, amount });
      setIsModalOpen(true);
    }
  };

  const handlePaymentConfirm = (paymentMethod: string, isAnonymous: boolean) => {
    if (selectedContribution) {
      const categoryId = categories.find(
        (c) => c.title === selectedContribution.category
      )?.id;

      if (categoryId) {
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === categoryId
              ? {
                  ...cat,
                  current: cat.current + selectedContribution.amount,
                  contributors: cat.contributors + 1,
                }
              : cat
          )
        );
      }

      setIsModalOpen(false);
      setShowSuccess(true);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSelectedContribution(null);
  };

  const culturalContext = {
    title: 'Understanding South Sudanese Dowry Tradition',
    description:
      'The dowry (bride price) is a sacred tradition in South Sudanese culture, representing the groom\'s family\'s respect for the bride\'s family and their commitment to the union. It is not a transaction but a covenant of honor.',
    points: [
      'Symbolizes the value and respect for the bride and her family lineage',
      'Creates a lasting bond between two families and their communities',
      'Demonstrates the groom\'s ability to provide and care for his wife',
      'Honors ancestors and cultural heritage passed through generations',
      'Represents a sacred covenant blessed by God and witnessed by community',
    ],
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="animate-pulse space-y-12">
            <div className="h-32 bg-surface rounded-lg" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="h-64 bg-surface rounded-lg" />
              <div className="h-64 bg-surface rounded-lg" />
              <div className="h-64 bg-surface rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalCurrent = categories.reduce((sum, cat) => sum + cat.current, 0);
  const totalGoal = categories.reduce((sum, cat) => sum + cat.goal, 0);
  const totalContributors = categories.reduce(
    (sum, cat) => sum + cat.contributors,
    0
  );

  return (
    <>
      <section className="w-full bg-background py-20 lg:py-28">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Support & Contributions
            </h1>
            <p className="font-inter text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Your generous contributions honor our cultural traditions and help
              us build a strong foundation for our marriage.
            </p>
          </div>

          <div className="mb-16 lg:mb-20">
            <ProgressTracker
              current={totalCurrent}
              goal={totalGoal}
              contributors={totalContributors}
              label="Overall Progress"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 mb-16 lg:mb-20">
            {categories.map((category) => (
              <ContributionCard
                key={category.id}
                title={category.title}
                description={category.description}
                icon={category.icon}
                suggestedAmounts={category.suggestedAmounts}
                onContribute={handleContribute}
                category={category.id}
              />
            ))}
          </div>

          <div className="mb-16 lg:mb-20">
            <CulturalContext
              title={culturalContext.title}
              description={culturalContext.description}
              points={culturalContext.points}
            />
          </div>

          <div className="bg-gradient-to-br from-accent/5 to-primary/5 rounded-2xl p-8 lg:p-12 border border-accent/10 text-center">
            <h3 className="font-playfair text-2xl lg:text-3xl font-semibold text-foreground mb-6">
              A Note of Gratitude
            </h3>
            <p className="font-inter text-base text-foreground/80 leading-relaxed max-w-3xl mx-auto mb-6">
              We are deeply humbled by your willingness to support our union. Your
              contributions are not just financial gifts—they are prayers,
              blessings, and expressions of love that will sustain us throughout
              our marriage.
            </p>
            <p className="font-inter text-sm text-muted-foreground italic">
              "Give, and it will be given to you. A good measure, pressed down,
              shaken together and running over, will be poured into your lap." -
              Luke 6:38
            </p>
          </div>
        </div>
      </section>

      {selectedContribution && (
        <>
          <PaymentModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            category={selectedContribution.category}
            amount={selectedContribution.amount}
            onConfirm={handlePaymentConfirm}
          />
          <SuccessMessage
            isVisible={showSuccess}
            onClose={handleCloseSuccess}
            category={selectedContribution.category}
            amount={selectedContribution.amount}
          />
        </>
      )}
    </>
  );
};

export default SupportInteractive;